import React, { useState, useEffect, useMemo, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { DateTime } from 'luxon';

function ClientDetails() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { csrfToken, fetchCsrfToken } = useAuth();
  const [usageLogs, setUsageLogs] = useState([]);
  const [membership, setMembership] = useState(null);
  const [profile, setProfile] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [editing, setEditing] = useState(false);
  const [editTier, setEditTier] = useState('');
  const [editEndDate, setEditEndDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [nuclearReason, setNuclearReason] = useState('');
  const gridRef = useRef(null);

  const columnDefs = useMemo(() => [
    { field: 'action', headerName: 'Action', filter: 'agTextColumnFilter', sortable: true, flex: 2 },
    {
      field: 'amount',
      headerName: 'Images',
      valueFormatter: (p) => (p.value || 0).toFixed(0),
      filter: 'agNumberColumnFilter',
      sortable: true,
      flex: 1
    },
    {
      field: 'financial_amount',
      headerName: 'Financial Amount',
      valueFormatter: (p) => `$${p.value?.toFixed(2) || 0.00}`,
      filter: 'agNumberColumnFilter',
      sortable: true,
      flex: 1
    },
    {
      field: 'created_at',
      headerName: 'Date',
      valueFormatter: (p) => p.value ? DateTime.fromISO(p.value).toLocaleString(DateTime.DATETIME_MED) : 'N/A',
      filter: 'agDateColumnFilter',
      sortable: true,
      flex: 2
    },
    {
      field: 'sale_name',
      headerName: 'Sale Name',
      valueFormatter: (p) => {
        try {
          let meta = p.data.meta;
          if (typeof meta === 'string') {
            meta = JSON.parse(meta || '{}');
          } else if (typeof meta !== 'object' || meta === null) {
            meta = {};
          }
          if (meta.salename && meta['sales section']) {
            return `${meta.salename} / ${meta['sales section']}`;
          }
        } catch (parseErr) {
          console.warn('Meta parse failed:', parseErr, p.data.meta);
        }
        return 'N/A';
      },
      filter: 'agTextColumnFilter',
      sortable: true,
      flex: 3
    },
    { field: 'source', headerName: 'Source', filter: 'agTextColumnFilter', sortable: true, flex: 1 },
  ], []);

  const defaultColDef = useMemo(() => ({
    minWidth: 100,
    filter: true,
    resizable: true,
    sortable: true,
  }), []);

  useEffect(() => {
    if (!clientId || clientId === 'undefined') {
      setError('Invalid client ID from route.');
      setLoading(false);
      return;
    }
    fetchTiers();
    fetchUsageLogs();
    fetchMembership();
    fetchProfile();
  }, [clientId]);

  const fetchTiers = async () => {
    let currentCsrf = csrfToken;
    try {
      if (!currentCsrf) currentCsrf = await fetchCsrfToken();
      const res = await fetch(`${process.env.REACT_APP_API_URL}/supabase/rpc/get_tiers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-csrf-token': currentCsrf },
        body: JSON.stringify({}),
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`Fetch tiers failed: ${res.statusText}`);
      const data = await res.json();
      // Filter for is_membership = true
      const membershipTiers = data.filter(t => t.is_membership === true);
      setTiers(membershipTiers || []);
    } catch (err) {
      console.error('Fetch tiers error:', err);
      // Fallback filtered to membership tiers
      setTiers([
        { tier: 'Starter Membership' },
        { tier: 'Professional' },
        { tier: 'Business Membership' }
      ]);
    }
  };

  const fetchWithDebug = async (url, bodyObj, fetchName) => {
    let currentCsrf = csrfToken;
    try {
      if (!currentCsrf) currentCsrf = await fetchCsrfToken();
      const body = JSON.stringify(bodyObj);
      const fullUrl = `${process.env.REACT_APP_API_URL}${url}`;
      const res = await fetch(fullUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-csrf-token': currentCsrf
        },
        body,
        credentials: 'include',
      });
      const text = await res.text();
      if (!res.ok) {
        throw new Error(`${fetchName} failed: ${res.status} ${res.statusText} - Body: ${text.substring(0, 200)}`);
      }
      if (text.trim() === '' || text.includes('[object Object]')) {
        throw new Error(`${fetchName} returned invalid content: ${text}`);
      }
      const data = JSON.parse(text);
      return data;
    } catch (err) {
      throw err;
    }
  };

  const fetchUsageLogs = async () => {
    setLoading(true);
    setError(null);
    setMessage(null);
    try {
      const data = await fetchWithDebug('/supabase/rpc/get_member_usage_log', { p_user_id: clientId }, 'Usage Logs');
      setUsageLogs(data || []);
    } catch (err) {
      setError(`Usage logs error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchMembership = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchWithDebug('/supabase/rpc/get_membership2', { p_user_id: clientId }, 'Membership');
      setMembership(data[0] || null);
      if (data[0]) {
        setEditTier(data[0].tier || '');
        setEditEndDate(data[0].end_date ? DateTime.fromISO(data[0].end_date).toFormat('yyyy-MM-dd') : '');
      }
    } catch (err) {
      setError(`Membership error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const data = await fetchWithDebug('/supabase/rpc/select_profile', { p_client_id: clientId }, 'Profile');
      setProfile(data[0] || null);
    } catch (err) {
      console.error(`Profile error: ${err.message}`);
      setProfile(null);
    } finally {
      setLoading(false);
    }
  };

  const toggleEdit = () => {
    if (editing) {
      setEditTier(membership.tier);
      setEditEndDate(membership.end_date ? DateTime.fromISO(membership.end_date).toFormat('yyyy-MM-dd') : '');
    }
    setEditing(!editing);
  };

  const handleSaveMembership = async () => {
    if (!editTier || !editEndDate) {
      setError('Please select a tier and end date.');
      return;
    }
    setLoading(true);
    setError(null);
    let currentCsrf = csrfToken;
    try {
      if (!currentCsrf) currentCsrf = await fetchCsrfToken();
      const res = await fetch(`${process.env.REACT_APP_API_URL}/supabase/rpc/update_membership2`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-csrf-token': currentCsrf },
        body: JSON.stringify({
          p_user_id: clientId,
          p_tier: editTier,
          p_end_date: editEndDate // yyyy-MM-dd; RPC casts to timestamptz
        }),
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`Update failed: ${res.statusText}`);
      setMessage('Membership updated successfully.');
      setEditing(false);
      fetchMembership();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitAdjust = async () => {
    if (!adjustAmount || parseInt(adjustAmount) <= 0 || !adjustReason) {
      setError('Please enter a positive integer amount and a reason.');
      return;
    }
    setLoading(true);
    setError(null);
    setMessage(null);
    let currentCsrf = csrfToken;
    try {
      if (!currentCsrf) currentCsrf = await fetchCsrfToken();
      const body = JSON.stringify({
        p_client_id: clientId,
        p_amount: parseInt(adjustAmount),
        p_reason: adjustReason
      });
      const res = await fetch(`${process.env.REACT_APP_API_URL}/supabase/rpc/admin_adjust_uploads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-csrf-token': currentCsrf },
        body,
        credentials: 'include',
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Upload adjustment failed: ${res.statusText} - ${text.substring(0, 200)}`);
      }
      const data = await res.json();
      setMessage('Upload availability bumped successfully.');
      setAdjustAmount('');
      setAdjustReason('');
      fetchUsageLogs();
      fetchMembership();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCashRefund = async () => {
    const amount = parseFloat(refundAmount) || 0;
    if (amount <= 0 || !refundReason) {
      setError('Please enter a positive refund amount and a reason.');
      return;
    }
    setLoading(true);
    setError(null);
    setMessage(null);
    let currentCsrf = csrfToken;
    try {
      if (!currentCsrf) currentCsrf = await fetchCsrfToken();
      const res = await fetch(`${process.env.REACT_APP_API_URL}/stripe/admin-refund-stripe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-csrf-token': currentCsrf },
        body: JSON.stringify({
          clientId,
          amount,
          reason: refundReason
        }),
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || `Cash refund failed: ${res.statusText}`);
      }
      setMessage(data.message || 'Cash refund issued successfully.');
      setRefundAmount('');
      setRefundReason('');
      fetchUsageLogs();
      fetchMembership();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRefundAndCancel = async () => {
    if (!nuclearReason) {
      setError('Please enter a reason for refund and cancel.');
      return;
    }
    setLoading(true);
    setError(null);
    setMessage(null);
    let currentCsrf = csrfToken;
    try {
      if (!currentCsrf) currentCsrf = await fetchCsrfToken();
      const res = await fetch(`${process.env.REACT_APP_API_URL}/stripe/admin-refund-and-cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-csrf-token': currentCsrf },
        body: JSON.stringify({
          clientId,
          reason: nuclearReason
        }),
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || `Refund and cancel failed: ${res.statusText}`);
      }
      setMessage('Membership refunded and canceled successfully.');
      setNuclearReason('');
      fetchUsageLogs();
      fetchMembership();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-100 p-6 space-y-6">
      <div className="flex justify-between">
        <button
          onClick={() => navigate('/admin/active-users')}
          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:opacity-50"
          disabled={loading}
        >
          Back to Active Users
        </button>
      </div>
      <div className="flex flex-col lg:flex-row gap-4">
        <div className="bg-white p-3 rounded-lg shadow flex-1">
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-lg font-semibold">Membership Details</h3>
            {!editing ? (
              <button
                onClick={toggleEdit}
                className="bg-gray-500 text-white px-3 py-1 rounded text-sm hover:bg-gray-600"
                disabled={loading}
              >
                Edit
              </button>
            ) : (
              <div className="flex space-x-2">
                <button
                  onClick={handleSaveMembership}
                  className="bg-green-500 text-white px-3 py-1 rounded text-sm hover:bg-green-600"
                  disabled={loading}
                >
                  Save
                </button>
                <button
                  onClick={toggleEdit}
                  className="bg-gray-500 text-white px-3 py-1 rounded text-sm hover:bg-gray-600"
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
          {profile && (
            <div className="mb-3 p-2 bg-gray-50 rounded text-sm">
              <p className="font-medium">
                Client: {profile.first_name} {profile.last_name} ({profile.email})
              </p>
            </div>
          )}
          {membership ? (
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Tier</label>
                {!editing ? (
                  <p className="font-medium">{membership.tier || 'None'}</p>
                ) : (
                  <select
                    value={editTier}
                    onChange={(e) => setEditTier(e.target.value)}
                    className="w-full border p-1 rounded text-sm"
                  >
                    <option value="">Select Tier</option>
                    {tiers.map((t) => (
                      <option key={t.tier} value={t.tier}>{t.tier}</option>
                    ))}
                  </select>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Start Date</label>
                <p className="text-gray-700">{membership.start_date ? DateTime.fromISO(membership.start_date).toLocaleString(DateTime.DATE_MED) : 'N/A'}</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">End Date</label>
                {!editing ? (
                  <p className="text-gray-700">{membership.end_date ? DateTime.fromISO(membership.end_date).toLocaleString(DateTime.DATE_MED) : 'N/A'}</p>
                ) : (
                  <input
                    type="date"
                    value={editEndDate}
                    onChange={(e) => setEditEndDate(e.target.value)}
                    className="w-full border p-1 rounded text-sm"
                  />
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Total Images Available</label>
                <p className="font-medium text-blue-600">{membership.total_images_avail || 0}</p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500">No membership found.</p>
          )}
        </div>
        <div className="bg-red-50 border border-red-200 p-3 rounded-lg flex-1 lg:flex-none">
          <label className="block text-xs font-medium text-red-700 mb-1">Refund/Cancel Membership</label>
          <div className="flex items-end space-x-2">
            <input
              type="text"
              value={nuclearReason}
              onChange={(e) => setNuclearReason(e.target.value)}
              placeholder="Reason for cancel"
              className="flex-1 border p-1 text-sm rounded"
            />
            <button
              onClick={handleRefundAndCancel}
              className="bg-red-600 text-white px-4 py-1 rounded text-sm hover:bg-red-700 disabled:opacity-50"
              disabled={loading || !nuclearReason}
            >
              Cancel & Refund
            </button>
          </div>
        </div>
      </div>
      <div className="flex flex-col md:flex-row space-y-6 md:space-y-0 md:space-x-6">
        <div className="bg-white p-4 rounded-lg shadow flex-1">
          <h3 className="text-lg font-semibold mb-2">Bump Images Available</h3>
          <div className="flex flex-col space-y-2 md:flex-row md:space-y-0 md:space-x-2 md:items-end text-sm">
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-700">Amount</label>
              <input
                type="number"
                value={adjustAmount}
                onChange={(e) => setAdjustAmount(e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
                min="0"
                step="1"
              />
            </div>
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-700">Reason</label>
              <input
                type="text"
                value={adjustReason}
                onChange={(e) => setAdjustReason(e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>
            <button
              onClick={handleSubmitAdjust}
              className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 disabled:opacity-50 text-sm"
              disabled={loading}
            >
              Bump Images
            </button>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow flex-1">
          <h3 className="text-lg font-semibold mb-2">Cash Refund (Stripe)</h3>
          <div className="flex flex-col space-y-2 md:flex-row md:space-y-0 md:space-x-2 md:items-end text-sm">
            <div className="flex-2">
              <label className="block text-xs font-medium text-gray-700">Refund Amount ($)</label>
              <input
                type="number"
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
                min="0"
                step="0.01"
                placeholder="Max: Paid amounts via Stripe"
              />
            </div>
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-700">Reason</label>
              <input
                type="text"
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>
            <button
              onClick={handleCashRefund}
              className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 disabled:opacity-50 text-sm"
              disabled={loading}
            >
              Issue Refund
            </button>
          </div>
        </div>
      </div>
      {loading && <p className="text-gray-500 text-center py-4">Loading...</p>}
      {error && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">{error}</div>}
      {message && <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">{message}</div>}
      <div className="ag-theme-quartz" style={{ height: 600, width: '100%' }}>
        <AgGridReact
          ref={gridRef}
          rowData={usageLogs}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          pagination={true}
          paginationPageSize={25}
          paginationPageSizeSelector={[25, 50, 100]}
          domLayout="normal"
        />
      </div>
    </div>
  );
}

export default ClientDetails;